// ======================================================
// FILE: src/pages/AdminAnalytics.jsx
// Changes:
// - Realtime now a small card (not full-width).
// - Adds small cards: Avg Time, Events.
// - Top Pages: show 6 + "See more" (up to 15).
// - Header actions match Admin page (Users, ← Back to dashboard).
// - Keeps Raw toggle. No global style changes.
// ======================================================

import React, { useEffect, useState } from "react";
import { auth } from "../firebase";
import SuperAdminNav from "../components/SuperAdminNav";

const RANGES = [7, 28, 90];

export default function AdminAnalytics() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);
  const [cards, setCards] = useState([]);
  const [showRaw, setShowRaw] = useState(false);
  const [showMorePages, setShowMorePages] = useState(false);

  useEffect(() => {
    const unsub = auth.onIdTokenChanged(async (u) => {
      if (!u) return setIsAdmin(false);
      try {
        const tok = await u.getIdTokenResult(true);

        let profile = {};
        try {
          const { getFirestore, doc, getDoc } = await import("firebase/firestore");
          const db = getFirestore();
          const snap = await getDoc(doc(db, "users", u.uid));
          profile = snap.exists() ? snap.data() : {};
        } catch {}

        setIsAdmin(
          !!(tok && tok.claims && tok.claims.admin) ||
          !!profile.isAdmin ||
          profile.role === "platform_admin" ||
          profile.tenantRole === "owner"
        );
      } catch {
        setIsAdmin(false);
      }
    });
    return () => { if (typeof unsub === "function") unsub(); };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, days]);

  async function load() {
    setLoading(true); setErr("");
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Sign in required.");
      const tok = await u.getIdToken();

      const r = await fetch(`/api/admin/analytics/summary?days=${days}`, {
        headers: { Authorization: `Bearer ${tok}` },
        credentials: "same-origin",
      });

      const txt = await r.text();
      if (!r.ok) {
        try {
          const j = JSON.parse(txt);
          const msg = j && j.error ? j.error : `${r.status} ${r.statusText}`;
          throw new Error(msg);
        } catch {
          throw new Error(txt || `${r.status} ${r.statusText}`);
        }
      }
      const j = JSON.parse(txt);
      const norm = normalizeSummary(j);
      setSummary(norm);
      setCards(toCards(norm));
    } catch (e) {
      setErr(e.message || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ad ad-wrap">
      <AdStyles />

      <SuperAdminNav title="Admin" active="analytics" />

      {/* Range + raw toggle */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <span className="ad-label" style={{ margin: 0 }}>Range:</span>
        {RANGES.map((d) => (
          <button
            key={d}
            className={"ad-tab " + (days === d ? "is-active" : "")}
            onClick={() => { setDays(d); setShowMorePages(false); }}
          >
            {d} days
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button className="ad-tab" onClick={() => setShowRaw((v) => !v)}>
          {showRaw ? "Hide raw JSON" : "Show raw JSON"}
        </button>
      </div>

      {err ? <div className="ad-banner ad-banner--error">{err}</div> : null}
      {loading ? <div className="ad-loading">Loading…</div> : null}

      {!loading && !err && summary ? (
        <>
          {/* KPI Row 1: Sessions, New Users, Active Users */}
          <section className="ad-detail-grid" style={{ marginTop: 8 }}>
            {cards.map((c) => (
              <div key={c.key} className="ad-card">
                <div className="ad-muted">{c.title}</div>
                <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
                  {c.value}
                </div>
              </div>
            ))}
          </section>

          {/* KPI Row 2: Realtime (mini), Avg Time (mini), Events (mini) */}
          <section className="ad-detail-grid" style={{ marginTop: 12 }}>
            <div className="ad-card">
              <div className="ad-muted">Realtime</div>
              <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
                {fmt(summary.realtime?.activeUsers)} online
              </div>
              {/* small hint */}
              <div className="ad-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Updates every GA few seconds
              </div>
            </div>

            <div className="ad-card">
              <div className="ad-muted">Average Time</div>
              <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
                {formatDuration(summary.totals?.avgSessionDurationSec)}
              </div>
            </div>

            <div className="ad-card">
              <div className="ad-muted">Events</div>
              <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
                {fmt(summary.totals?.events)}
              </div>
            </div>
          </section>

          {/* Top Pages (6 + see more up to 15) */}
          <section className="ad-card" style={{ marginTop: 12 }}>
            <h3>Top Pages</h3>
            <SimpleTable
              columns={[{ key: "path", label: "Path" }, { key: "pageViews", label: "Views" }]}
              rows={(summary.topPages || []).slice(0, showMorePages ? 15 : 6)}
              empty="No page data."
            />
            {(summary.topPages || []).length > 6 && (
              <div style={{ marginTop: 8 }}>
                <button className="ad-tab" onClick={() => setShowMorePages(v => !v)}>
                  {showMorePages ? "See less" : "See more"}
                </button>
              </div>
            )}
          </section>

          {/* Countries / Devices / Source-Medium */}
          <section className="ad-detail-grid" style={{ marginTop: 12 }}>
            <div className="ad-card">
              <h3>Countries</h3>
              <SimpleTable
                columns={[{ key: "country", label: "Country" }, { key: "sessions", label: "Sessions" }]}
                rows={(summary.countries || [])}
                empty="No country data."
              />
            </div>

            <div className="ad-card">
              <h3>Devices</h3>
              <SimpleTable
                columns={[{ key: "deviceCategory", label: "Device" }, { key: "sessions", label: "Sessions" }]}
                rows={(summary.devices || [])}
                empty="No device data."
              />
            </div>

            <div className="ad-card">
              <h3>Source / Medium</h3>
              <SimpleTable
                columns={[
                  { key: "source", label: "Source" },
                  { key: "medium", label: "Medium" },
                  { key: "sessions", label: "Sessions" },
                ]}
                rows={(summary.sources || [])}
                empty="No source/medium data."
              />
            </div>
          </section>

          {/* Raw (collapsed by default) */}
          {showRaw && (
            <section className="ad-card" style={{ marginTop: 12 }}>
              <h3>Raw</h3>
              <pre style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}>
                {JSON.stringify(summary.raw, null, 2)}
              </pre>
            </section>
          )}
        </>
      ) : null}
    </div>
  );
}

// ---------- UI helpers ----------
function SimpleTable({ columns, rows, empty }) {
  const data = Array.isArray(rows) ? rows : [];
  if (!data.length) return <div className="ad-muted">{empty}</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="ad-table">
        <thead>
          <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => <td key={c.key}>{renderCell(r[c.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderCell(v) {
  if (typeof v === "number") return fmt(v);
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function fmt(n) {
  const x = +n || 0;
  return x >= 1000 ? x.toLocaleString() : String(Math.round(x));
}

function formatDuration(sec) {
  const s = Math.max(0, Math.floor(+sec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

// ---------- Normalization ----------
function normalizeSummary(raw) {
  const r = raw || {};
  const metrics = r.metrics || {};
  const series = Array.isArray(r.series) ? r.series : [];

  const keys = ["sessions", "newUsers", "activeUsers", "pageViews", "avgSessionDurationSec", "eventCount"];
  const hasAny = keys.some((k) => typeof metrics[k] === "number" && metrics[k] > 0);

  let totals = metrics;
  if (!hasAny) {
    let sessions = 0, newUsers = 0, pageViews = 0, events = 0, activeUsersMax = 0, durSum = 0, durCount = 0;
    for (const row of series) {
      sessions += +row.sessions || 0;
      newUsers += +row.newUsers || 0;
      pageViews += +row.pageViews || +row.screenPageViews || 0;
      events += +row.eventCount || 0;
      activeUsersMax = Math.max(activeUsersMax, +row.activeUsers || 0);
      if (+row.avgSessionDurationSec > 0) { durSum += +row.avgSessionDurationSec; durCount++; }
    }
    totals = {
      sessions,
      newUsers,
      activeUsers: activeUsersMax,
      pageViews,
      avgSessionDurationSec: durCount ? Math.round(durSum / durCount) : 0,
      eventCount: events,
    };
  }

  return {
    raw: r,
    totals: {
      sessions: +totals.sessions || 0,
      newUsers: +totals.newUsers || 0,
      activeUsers: +totals.activeUsers || 0,
      pageViews: +totals.pageViews || 0,
      avgSessionDurationSec: +totals.avgSessionDurationSec || 0,
      events: +totals.eventCount || 0, // map for UI
    },
    topPages: r.topPages || [],
    countries: r.countries || [],
    devices: r.devices || [],
    sources: r.sources || [],
    realtime: r.realtime || { activeUsers: 0, topPaths: [] },
  };
}

function toCards(s) {
  const t = (s && s.totals) || {};
  return [
    { key: "sessions",    title: "Sessions",     value: fmt(t.sessions) },
    { key: "newUsers",    title: "New Users",    value: fmt(t.newUsers) },
    { key: "activeUsers", title: "Active Users", value: fmt(t.activeUsers) },
  ];
}

function AdStyles() {
  return (
    <style>{`
      .ad *, .ad *::before, .ad *::after { box-sizing: border-box; }
      .ad-wrap{max-width:1200px;margin:24px auto;padding:0 16px;width:100%}
      .ad-header{display:flex;justify-content:space-between;align-items:center;gap:12px;margin:8px 0 16px}
      .ad-tabs{display:flex;gap:8px;flex-wrap:wrap}
      .ad-tab{padding:10px 14px;border:1px solid #d6d6e7;border-radius:10px;background:#fff;cursor:pointer;line-height:1;text-decoration:none;color:inherit}
      .ad-tab.is-active{background:#111827;color:#fff;border-color:#111827}
      .ad-label{font-weight:600;color:#374151;display:inline-flex;align-items:center}
      .ad-card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:12px;min-width:0}
      .ad-detail-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:16px}
      .ad-loading{padding:14px;text-align:center}
      .ad-banner{padding:10px 12px;border-radius:10px;margin:8px 0;border:1px solid}
      .ad-banner--error{background:#fee2e2;color:#7a0b0b;border-color:#fecaca}
      .ad-muted{color:#6b7280}
      .ad-table{width:100%;border-collapse:separate;border-spacing:0 6px}
      .ad-table th{font-weight:600;text-align:left;color:#374151;padding:6px 8px}
      .ad-table td{background:#fafafa;border:1px solid #eee;border-left:none;border-right:none;padding:8px;border-radius:6px}
      .ad-table tr td:first-child{border-left:1px solid #eee;border-top-left-radius:8px;border-bottom-left-radius:8px}
      .ad-table tr td:last-child{border-right:1px solid #eee;border-top-right-radius:8px;border-bottom-right-radius:8px}
      @media (max-width: 480px){ .ad-detail-grid{gap:12px} }
    `}</style>
  );
}
